import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Settings2, Volume2, VolumeX } from 'lucide-react';
import { VoiceCoach, loadVoiceSettings, saveVoiceSettings } from '@/lib/voiceCoach';
import { buildStrengthPlan, buildCircuitPlan, planTexts, useGuidedEngine } from './useGuidedEngine';
import { IntroView, SetupView, WorkView, RestView, SummaryView, CoachSettings } from './GuidedViews';
import { ModeChooser, ModeTutorial, MODE_TUTORIAL_KEY } from './GuidedModes';

// kind: 'strength' | 'circuit' | 'race' — one player, one engine, one set of voice settings for all three.
export function GuidedWorkout({ open, onOpenChange, workout, exMap, onSave, kind = 'strength' }) {
  if (!open) return null;
  const guardedChange = (o) => { if (!o && !window.confirm('Leave the guided workout? Progress in this session will be lost.')) return; onOpenChange(o); };
  return (
    <Dialog open={open} onOpenChange={guardedChange}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-md max-h-[95vh] overflow-y-auto p-5" data-testid="guided-player"
        onInteractOutside={e => e.preventDefault()}>
        <DialogHeader className="sr-only"><DialogTitle>{workout.name} guided session</DialogTitle></DialogHeader>
        <Player workout={workout} exMap={exMap} onSave={onSave} kind={kind} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function Player({ workout, exMap, onSave, onClose, kind }) {
  const [settings, setSettings] = useState(loadVoiceSettings);
  const coachRef = useRef(null);
  if (!coachRef.current) coachRef.current = new VoiceCoach(settings);
  const coach = coachRef.current;
  const mode = settings.strengthMode === 'manual' ? 'manual' : 'continuous';
  const [tutorial, setTutorial] = useState(() => kind === 'strength' && !localStorage.getItem(MODE_TUTORIAL_KEY));
  const plan = useMemo(() => (kind === 'strength'
    ? buildStrengthPlan(workout.items || [], exMap, { motivation: settings.motivation, mode, rate: settings.rate })
    : buildCircuitPlan(workout, exMap, { motivation: settings.motivation, race: kind === 'race', rate: settings.rate })), [mode]);
  const eng = useGuidedEngine({ plan, coach, settings });
  const [showSet, setShowSet] = useState(false);
  const [prog, setProg] = useState({ loaded: 0, total: 1, ready: !settings.enabled });
  const [weights, setWeights] = useState(() => (workout.items || []).map(it => it.weight || ''));
  const [saving, setSaving] = useState(false);

  const set = (patch) => setSettings(s => { const n = { ...s, ...patch }; saveVoiceSettings(n); coach.update(n); return n; });
  const closeTutorial = () => { localStorage.setItem(MODE_TUTORIAL_KEY, '1'); setTutorial(false); };

  // Prefetch the first set before starting; everything else keeps loading in the background.
  useEffect(() => {
    coach.update(settings);
    if (!settings.enabled) { setProg(p => ({ ...p, ready: true })); return undefined; }
    let alive = true;
    const p0 = plan.exercises[0];
    const first = p0 ? [p0.setup, ...p0.setEvents[0].events.flatMap(e => [e.text, e.plain])].filter(Boolean) : [];
    const rest = [...plan.exercises.flatMap(planTexts), plan.done];
    const total = new Set(first).size;
    setProg({ loaded: 0, total, ready: false });
    let n = 0;
    const bump = () => alive && setProg(p => ({ ...p, loaded: Math.min(total, ++n) }));
    const live = () => alive;
    coach.prefetch(first, bump, live).then(() => { if (alive) setProg(p => ({ ...p, ready: true })); return coach.prefetch(rest, null, live); });
    return () => { alive = false; };
  }, [settings.enabled, settings.voice, plan]);
  useEffect(() => () => coach.dispose(), []);

  const save = async () => {
    setSaving(true);
    const items = (workout.items || []).map((it, i) => {
      const steps = plan.exercises.map((p, k) => ({ p, k })).filter(({ p }) => p.idx === i);
      const done = steps.reduce((s, { k }) => s + eng.setsDone[k], 0);
      const total = steps.reduce((s, { p }) => s + p.sets, 0);
      const target = steps[0]?.p.target;
      const unitless = ['open', 'timed'].includes(steps[0]?.p.tempo.kind);
      return kind === 'strength'
        ? { ...it, done: done >= total, sets_done: done, reps_done: unitless ? it.reps : target, weight_used: weights[i] || '' }
        : { ...it, done: done >= total, rounds: plan.rounds, rounds_done: done, reps_done: unitless ? it.reps : (target ?? it.reps) };
    });
    const ids = items.map((it, i) => (it.done ? String(i) : null)).filter(Boolean);
    try { await onSave({ performed_items: items, completed_exercise_ids: ids, duration_seconds: eng.durationSec }); onClose(); }
    finally { setSaving(false); }
  };

  const { st, cur } = eng;
  const next = plan.exercises[st.ex + 1];
  const nextLabel = st.set + 1 < cur?.sets ? `Set ${st.set + 2} of ${cur.name}` : next ? `${next.round && next.round !== cur.round ? `Round ${next.round} · ` : ''}${next.name}` : 'Finish';
  const modeSlot = kind === 'strength' && (tutorial ? <div className="mt-5"><ModeTutorial onClose={closeTutorial} /></div>
    : <ModeChooser mode={mode} setMode={m => set({ strengthMode: m })} onHelp={() => setTutorial(true)} />);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="min-w-0">
          <div className="label-mini text-[#00C8FF] truncate">{workout.name}</div>
          {st.phase !== 'intro' && st.phase !== 'done' && (
            <div className="text-xs text-muted-foreground font-mono" data-testid="guided-progress">
              {cur.round ? `Round ${cur.round} of ${cur.rounds} · ${kind === 'race' ? 'Station' : 'Exercise'} ${cur.station + 1} of ${cur.stations}` : `Exercise ${st.ex + 1} of ${plan.exercises.length}`}
              {kind === 'strength' && ` · ${plan.continuous ? 'Continuous' : 'Manual'}`}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={() => set({ enabled: !settings.enabled })} data-testid="guided-voice-toggle" className="w-9 h-9 rounded-full hover:bg-[var(--c-film)] flex items-center justify-center" title="Voice on/off">
            {settings.enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
          </button>
          <button onClick={() => setShowSet(v => !v)} data-testid="guided-settings-btn" className="w-9 h-9 rounded-full hover:bg-[var(--c-film)] flex items-center justify-center" title="Coach settings"><Settings2 className="w-4 h-4" /></button>
        </div>
      </div>
      {showSet && <CoachSettings s={settings} set={set} onPreview={() => setTimeout(() => coach.say("Hi, I'm your YourReGen coach. Let's train.", 'interrupt'), 0)} />}
      {st.phase === 'intro' && <IntroView {...prog} locked={tutorial} voiceOn={settings.enabled} onBegin={eng.begin} kind={kind} modeSlot={modeSlot} />}
      {st.phase === 'setup' && <SetupView cur={cur} weight={weights[cur.idx]} setWeight={v => setWeights(w => w.map((x, i) => (i === cur.idx ? v : x)))}
        onReady={eng.ready} onRepeat={eng.repeat} onSkip={eng.skipExercise} auto={eng.auto} eng={eng} />}
      {st.phase === 'work' && <WorkView eng={eng} />}
      {st.phase === 'rest' && <RestView eng={eng} nextLabel={nextLabel} />}
      {st.phase === 'done' && <SummaryView plan={plan} setsDone={eng.setsDone} weights={weights} durationSec={eng.durationSec} saving={saving} onSave={save} onClose={onClose} />}
    </div>
  );
}
